import React, { useMemo, useState } from 'react';

import { datasetsUtils } from 'app-components/flux/datasets';
import type { Dataset } from 'app-components/flux/datasets/datasetsTypes';
import type { OrderedMap } from 'immutable';

import CheckIcon from '@mui/icons-material/Check';
import Button from '@mui/material/Button';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';

import Dump from 'components/Dump';

interface MiniDatasetPickerProps {
  dataset: Dataset | undefined;
  datasets: OrderedMap<string, Dataset>;
  label: string;
  onChange: (newDataset: Dataset) => void;
  id: string;
}

const MiniDatasetPicker = ({ datasets, dataset, id, label, onChange }: MiniDatasetPickerProps) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | Element>(null);
  const sortedDataset = useMemo(
    () => datasets.valueSeq().sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [datasets],
  );
  const handleClick = (event: React.PointerEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuItemClick = (value: Dataset) => {
    setAnchorEl(null);
    if (value) {
      onChange(value);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        aria-owns={anchorEl ? 'dataset-menu' : undefined}
        aria-haspopup='true'
        onClick={handleClick}
        style={{ height: '100%' }}
        id={id}
      >
        {label || 'CHANGE'}
      </Button>
      <Menu
        id='dataset-menu'
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        style={{ zIndex: theme.zIndex.modal + 2 }}
      >
        {sortedDataset.map((theDataset, index) => (
          <MenuItem
            key={theDataset.id}
            selected={dataset === theDataset}
            onClick={() => handleMenuItemClick(theDataset)}
            disabled={!datasetsUtils.hasActiveEntitlement(theDataset)}
          >
            <Dump value={theDataset} />
            <ListItemIcon
              style={{
                visibility: `${dataset === theDataset ? 'visible' : 'hidden'}`,
                marginRight: 0,
              }}
            >
              <CheckIcon style={{ color: theme.palette.primary.main }} />
            </ListItemIcon>
            <ListItemText
              primaryTypographyProps={{
                variant: 'paragraphRegular',
                color: dataset === theDataset ? theme.palette.primary.main : theme.palette.text.primary,
              }}
              primary={theDataset.name}
              id={index === 0 ? 'my-account-1st-data-set' : ''}
              data-testid={theDataset?.id}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default MiniDatasetPicker;
